/**
 * Test Prompt Modal
 * Interactive prompt testing with optimization and evaluation
 */

import { outreachApi } from '../services/outreach-api';

// Types
interface TestResult {
  id: string;
  output: string;
  tokens: { input: number; output: number; total: number };
  cost: number;
  model: string;
}

interface OptimizeResult {
  originalPrompt: string;
  optimizedPrompt: string;
  issues: Array<{
    type: string;
    severity: string;
    description: string;
    suggestion: string;
  }>;
  suggestions: string[];
  improvementSummary: string;
}

interface EvalResult {
  score: number;
  passed: boolean;
  feedback: string;
  criteriaScores: Array<{
    name: string;
    score: number;
    feedback: string;
  }>;
}

interface LogEntry {
  id: string;
  timestamp: number;
  prompt: string;
  input: string;
  output: string;
  score?: number;
  passed?: boolean;
  tokens: { input: number; output: number; total: number };
  cost?: number;
  model: string;
}

export class TestPromptModal {
  private container: HTMLElement | null = null;
  private isOpen = false;

  // State
  private prompt = '';
  // Input fields (structured like Chrome extension)
  private postAuthor = '';
  private postContent = '';
  private postUrl = '';
  private postType: 'original' | 'reshare' | 'article' = 'original';
  private existingComments = '';
  // Old single input field (for backwards compat)
  private input = '';
  private platform = 'linkedin';
  private promptId = '';
  private promptVersion = '';
  private output = '';
  private reasoning = '';
  private lastResult: TestResult | null = null;
  private evalResult: EvalResult | null = null;
  private optimizeResult: OptimizeResult | null = null;
  private isLoading = false;
  private loadingAction = '';
  private activeTab: 'test' | 'log' = 'test';
  private logEntries: LogEntry[] = [];

  constructor() {
    this.loadLastState();
  }

  private async loadLastState(): Promise<void> {
    try {
      const state = await outreachApi.getTestState() as any;
      if (state) {
        this.prompt = state.prompt || '';
        this.input = state.input || '';
        this.postAuthor = state.postAuthor || '';
        this.postContent = state.postContent || '';
        this.postUrl = state.postUrl || '';
        this.postType = state.postType || 'original';
        this.existingComments = state.existingComments || '';
        this.platform = state.platform || 'linkedin';
        this.promptId = state.promptId || '';
        this.promptVersion = state.promptVersion || '';
      }
    } catch (e) {
      // Ignore load errors
    }
  }

  private async saveState(): Promise<void> {
    try {
      await outreachApi.saveTestState({
        prompt: this.prompt,
        input: this.input,
        postAuthor: this.postAuthor,
        postContent: this.postContent,
        postUrl: this.postUrl,
        postType: this.postType,
        existingComments: this.existingComments,
        platform: this.platform,
        promptId: this.promptId,
        promptVersion: this.promptVersion
      } as any);
    } catch (e) {
      // Ignore save errors
    }
  }

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.render();
    this.attachListeners();
    // Load log when opening
    this.loadLog();
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.saveState();
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }

  private render(): void {
    // Remove existing modal if any
    const existing = document.getElementById('test-prompt-modal');
    if (existing) existing.remove();

    this.container = document.createElement('div');
    this.container.id = 'test-prompt-modal';
    this.container.className = 'modal-overlay';
    this.container.innerHTML = this.getHTML();
    document.body.appendChild(this.container);
  }

  private getHTML(): string {
    return `
      <div class="modal-content test-prompt-modal">
        <div class="modal-header">
          <h2>Test Prompt</h2>
          <div class="modal-tabs">
            <button class="modal-tab ${this.activeTab === 'test' ? 'active' : ''}" data-tab="test">Test</button>
            <button class="modal-tab ${this.activeTab === 'log' ? 'active' : ''}" data-tab="log">Log</button>
          </div>
          <button class="modal-close" id="test-prompt-close">&times;</button>
        </div>

        ${this.activeTab === 'test' ? this.getTestTabHTML() : this.getLogTabHTML()}
      </div>
    `;
  }

  private getTestTabHTML(): string {
    return `
      <div class="test-prompt-body">
        <!-- Left Panel: Inputs -->
        <div class="test-prompt-left">
          <div class="test-prompt-section">
            <label>Prompt</label>
            <textarea id="test-prompt-textarea" placeholder="Enter your prompt template...
Use {{postContent}} for the input text, {{platform}} for platform name.">${this.escapeHtml(this.prompt)}</textarea>
            <div class="test-prompt-actions-row">
              <button class="btn-secondary btn-sm" id="btn-optimize" ${this.isLoading ? 'disabled' : ''}>
                ${this.loadingAction === 'optimize' ? 'Optimizing...' : 'Optimize'}
              </button>
            </div>
            <div class="test-prompt-actions-row" style="margin-top: 8px;">
              <input type="text" id="test-prompt-id" placeholder="Prompt ID (pmpt_...)" value="${this.escapeHtml(this.promptId)}" class="input-sm" style="flex: 2;">
              <input type="text" id="test-prompt-version" placeholder="Version" value="${this.escapeHtml(this.promptVersion)}" class="input-sm" style="flex: 0.5; max-width: 80px;">
              ${this.promptId.startsWith('pmpt_') ? '<span class="stored-prompt-badge">Stored Prompt</span>' : ''}
            </div>
          </div>

          <!-- Structured Input Fields (like Chrome extension) -->
          <div class="test-prompt-section test-prompt-row">
            <div class="test-prompt-field" style="flex: 1;">
              <label>Author</label>
              <input type="text" id="test-post-author" placeholder="Post author name" value="${this.escapeHtml(this.postAuthor)}">
            </div>
            <div class="test-prompt-field" style="flex: 0.8;">
              <label>Post Type</label>
              <select id="test-post-type">
                <option value="original" ${this.postType === 'original' ? 'selected' : ''}>Original</option>
                <option value="reshare" ${this.postType === 'reshare' ? 'selected' : ''}>Reshare</option>
                <option value="article" ${this.postType === 'article' ? 'selected' : ''}>Article</option>
              </select>
            </div>
          </div>

          <div class="test-prompt-section">
            <label>Post Content</label>
            <textarea id="test-post-content" placeholder="Enter the post content...">${this.escapeHtml(this.postContent)}</textarea>
          </div>

          <div class="test-prompt-section test-prompt-row">
            <div class="test-prompt-field" style="flex: 1;">
              <label>Post URL (optional)</label>
              <input type="text" id="test-post-url" placeholder="https://linkedin.com/..." value="${this.escapeHtml(this.postUrl)}">
            </div>
          </div>

          <div class="test-prompt-section">
            <label>Existing Comments (optional)</label>
            <textarea id="test-existing-comments" placeholder="Paste existing comments to avoid repetition..." style="min-height: 60px;">${this.escapeHtml(this.existingComments)}</textarea>
          </div>

          <div class="test-prompt-section test-prompt-row">
            <div class="test-prompt-field">
              <label>Platform</label>
              <select id="test-platform-select">
                <option value="linkedin" ${this.platform === 'linkedin' ? 'selected' : ''}>LinkedIn</option>
                <option value="instagram" ${this.platform === 'instagram' ? 'selected' : ''}>Instagram</option>
                <option value="twitter" ${this.platform === 'twitter' ? 'selected' : ''}>Twitter</option>
                <option value="youtube" ${this.platform === 'youtube' ? 'selected' : ''}>YouTube</option>
                <option value="skool" ${this.platform === 'skool' ? 'selected' : ''}>Skool</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Right Panel: Output -->
        <div class="test-prompt-right">
          ${this.getOutputHTML()}
        </div>
      </div>

      <div class="test-prompt-footer">
        <div class="test-prompt-footer-left">
          <button class="btn-primary" id="btn-run-test" ${this.isLoading ? 'disabled' : ''}>
            ${this.loadingAction === 'test' ? 'Running...' : 'Run Test'} (Ctrl+Enter)
          </button>
          <button class="btn-secondary" id="btn-evaluate" ${this.isLoading || !this.output ? 'disabled' : ''}>
            ${this.loadingAction === 'evaluate' ? 'Evaluating...' : 'Evaluate'}
          </button>
        </div>
        <div class="test-prompt-footer-right">
          ${this.lastResult ? `
            <span class="test-meta">Tokens: ${this.lastResult.tokens.total}</span>
            <span class="test-meta">Cost: $${this.lastResult.cost.toFixed(6)}</span>
            <span class="test-meta">${this.lastResult.model}</span>
          ` : ''}
        </div>
      </div>
    `;
  }

  private getOutputHTML(): string {
    if (this.isLoading && this.loadingAction === 'test') {
      return `<div class="test-output-loading">Generating...</div>`;
    }

    if (this.optimizeResult) {
      return this.getOptimizeResultHTML();
    }

    if (!this.output) {
      return `<div class="test-output-empty">Run a test to see output here</div>`;
    }

    return `
      <div class="test-output-section">
        <div class="test-output-header">
          <label>Output</label>
          <button class="btn-icon btn-sm" id="btn-copy-output" title="Copy">
            <span class="material-symbols-outlined">content_copy</span>
          </button>
        </div>
        <div class="test-output-content">${this.escapeHtml(this.output)}</div>
        <div class="test-output-length">${this.output.length} chars</div>
      </div>

      ${this.evalResult ? this.getEvalResultHTML() : ''}
    `;
  }

  private getEvalResultHTML(): string {
    if (!this.evalResult) return '';

    const scoreClass = this.evalResult.passed ? 'score-pass' : 'score-fail';

    return `
      <div class="test-eval-section">
        <div class="test-eval-header">
          <span class="test-eval-score ${scoreClass}">
            Score: ${this.evalResult.score}/10 ${this.evalResult.passed ? 'PASS' : 'FAIL'}
          </span>
        </div>
        <div class="test-eval-feedback">${this.escapeHtml(this.evalResult.feedback)}</div>
        <div class="test-eval-criteria">
          ${this.evalResult.criteriaScores.map(cs => `
            <div class="test-eval-criterion">
              <span class="criterion-name">${cs.name}</span>
              <span class="criterion-score">${cs.score}/10</span>
              <span class="criterion-feedback">${this.escapeHtml(cs.feedback)}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  private getOptimizeResultHTML(): string {
    if (!this.optimizeResult) return '';

    return `
      <div class="test-optimize-section">
        <div class="test-optimize-header">
          <label>Optimization Results</label>
          <button class="btn-secondary btn-sm" id="btn-apply-optimized">Apply</button>
          <button class="btn-secondary btn-sm" id="btn-dismiss-optimize">Dismiss</button>
        </div>

        ${this.optimizeResult.issues.length > 0 ? `
          <div class="test-optimize-issues">
            <h4>Issues Found (${this.optimizeResult.issues.length})</h4>
            ${this.optimizeResult.issues.map(issue => `
              <div class="optimize-issue ${issue.severity}">
                <span class="issue-type">${issue.type.replace('_', ' ')}</span>
                <span class="issue-severity">${issue.severity}</span>
                <p class="issue-description">${this.escapeHtml(issue.description)}</p>
                <p class="issue-suggestion">${this.escapeHtml(issue.suggestion)}</p>
              </div>
            `).join('')}
          </div>
        ` : '<p class="no-issues">No issues found!</p>'}

        <div class="test-optimize-summary">
          <h4>Summary</h4>
          <p>${this.escapeHtml(this.optimizeResult.improvementSummary)}</p>
        </div>

        <div class="test-optimize-prompt">
          <h4>Optimized Prompt</h4>
          <pre>${this.escapeHtml(this.optimizeResult.optimizedPrompt)}</pre>
        </div>
      </div>
    `;
  }

  private getLogTabHTML(): string {
    if (this.logEntries.length === 0) {
      return `
        <div class="test-log-empty">
          <p>No test history yet</p>
        </div>
      `;
    }

    return `
      <div class="test-log-container">
        <div class="test-log-header">
          <span>${this.logEntries.length} entries</span>
          <button class="btn-secondary btn-sm" id="btn-clear-log">Clear All</button>
        </div>
        <div class="test-log-list">
          ${this.logEntries.map(entry => `
            <div class="test-log-entry" data-id="${entry.id}">
              <div class="log-entry-header">
                <span class="log-entry-date">${this.formatDate(entry.timestamp)}</span>
                <span class="log-entry-model">${entry.model}</span>
                ${entry.score !== undefined ? `
                  <span class="log-entry-score ${entry.passed ? 'score-pass' : 'score-fail'}">
                    ${entry.score}/10
                  </span>
                ` : ''}
                <button class="btn-icon btn-sm log-entry-delete" data-id="${entry.id}" title="Delete">
                  <span class="material-symbols-outlined">delete</span>
                </button>
              </div>
              <div class="log-entry-input">${this.escapeHtml(entry.input.slice(0, 100))}${entry.input.length > 100 ? '...' : ''}</div>
              <div class="log-entry-output">${this.escapeHtml(entry.output.slice(0, 150))}${entry.output.length > 150 ? '...' : ''}</div>
              <div class="log-entry-meta">
                <span>Tokens: ${entry.tokens.total}</span>
                ${entry.cost ? `<span>Cost: $${entry.cost.toFixed(6)}</span>` : ''}
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  private attachListeners(): void {
    if (!this.container) return;

    // Close button
    this.container.querySelector('#test-prompt-close')?.addEventListener('click', () => this.close());

    // Click outside to close
    this.container.addEventListener('click', (e) => {
      if (e.target === this.container) this.close();
    });

    // Tab switching
    this.container.querySelectorAll('.modal-tab').forEach(tab => {
      tab.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        this.activeTab = target.dataset.tab as 'test' | 'log';
        this.render();
        this.attachListeners();
      });
    });

    // Input handlers
    const promptTextarea = this.container.querySelector('#test-prompt-textarea') as HTMLTextAreaElement;
    const postAuthorInput = this.container.querySelector('#test-post-author') as HTMLInputElement;
    const postContentTextarea = this.container.querySelector('#test-post-content') as HTMLTextAreaElement;
    const postUrlInput = this.container.querySelector('#test-post-url') as HTMLInputElement;
    const postTypeSelect = this.container.querySelector('#test-post-type') as HTMLSelectElement;
    const existingCommentsTextarea = this.container.querySelector('#test-existing-comments') as HTMLTextAreaElement;
    const platformSelect = this.container.querySelector('#test-platform-select') as HTMLSelectElement;
    const promptIdInput = this.container.querySelector('#test-prompt-id') as HTMLInputElement;
    const promptVersionInput = this.container.querySelector('#test-prompt-version') as HTMLInputElement;

    promptTextarea?.addEventListener('input', (e) => {
      this.prompt = (e.target as HTMLTextAreaElement).value;
    });

    postAuthorInput?.addEventListener('input', (e) => {
      this.postAuthor = (e.target as HTMLInputElement).value;
    });

    postContentTextarea?.addEventListener('input', (e) => {
      this.postContent = (e.target as HTMLTextAreaElement).value;
    });

    postUrlInput?.addEventListener('input', (e) => {
      this.postUrl = (e.target as HTMLInputElement).value;
    });

    postTypeSelect?.addEventListener('change', (e) => {
      this.postType = (e.target as HTMLSelectElement).value as 'original' | 'reshare' | 'article';
    });

    existingCommentsTextarea?.addEventListener('input', (e) => {
      this.existingComments = (e.target as HTMLTextAreaElement).value;
    });

    platformSelect?.addEventListener('change', (e) => {
      this.platform = (e.target as HTMLSelectElement).value;
    });

    promptIdInput?.addEventListener('input', (e) => {
      const oldValue = this.promptId;
      this.promptId = (e.target as HTMLInputElement).value;
      // Re-render if we switched between stored/inline mode
      if (oldValue.startsWith('pmpt_') !== this.promptId.startsWith('pmpt_')) {
        this.render();
        this.attachListeners();
      }
    });

    promptVersionInput?.addEventListener('input', (e) => {
      this.promptVersion = (e.target as HTMLInputElement).value;
    });

    // Keyboard shortcut: Ctrl+Enter to run test
    this.container.addEventListener('keydown', (e) => {
      if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault();
        this.runTest();
      }
    });

    // Action buttons
    this.container.querySelector('#btn-run-test')?.addEventListener('click', () => this.runTest());
    this.container.querySelector('#btn-optimize')?.addEventListener('click', () => this.optimizePrompt());
    this.container.querySelector('#btn-evaluate')?.addEventListener('click', () => this.evaluateOutput());
    this.container.querySelector('#btn-copy-output')?.addEventListener('click', () => this.copyOutput());
    this.container.querySelector('#btn-apply-optimized')?.addEventListener('click', () => this.applyOptimized());
    this.container.querySelector('#btn-dismiss-optimize')?.addEventListener('click', () => this.dismissOptimize());
    this.container.querySelector('#btn-clear-log')?.addEventListener('click', () => this.clearLog());

    // Log entry delete buttons
    this.container.querySelectorAll('.log-entry-delete').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (e.currentTarget as HTMLElement).dataset.id;
        if (id) this.deleteLogEntry(id);
      });
    });
  }

  /**
   * Build structured input like Chrome extension does
   */
  private buildStructuredInput(): string {
    const parts: string[] = [];

    if (this.postAuthor) {
      parts.push(`Author: ${this.postAuthor}`);
    }
    if (this.platform) {
      parts.push(`Platform: ${this.platform}`);
    }
    if (this.postType && this.postType !== 'original') {
      parts.push(`Post Type: ${this.postType}`);
    }
    if (this.postUrl) {
      parts.push(`URL: ${this.postUrl}`);
    }
    if (this.postContent) {
      parts.push(`\nPost Content:\n${this.postContent}`);
    }
    if (this.existingComments) {
      parts.push(`\nExisting Comments:\n${this.existingComments}`);
    }

    return parts.join('\n');
  }

  private async runTest(): Promise<void> {
    const useStoredPrompt = this.promptId.startsWith('pmpt_');

    // For stored prompts, only input is required
    // For inline prompts, both prompt and input are required
    if (!useStoredPrompt && !this.prompt) {
      alert('Please enter a prompt template');
      return;
    }
    if (!this.postContent) {
      alert('Please enter post content');
      return;
    }

    // Build structured input from fields
    const structuredInput = this.buildStructuredInput();
    this.input = structuredInput; // Keep for backwards compat

    this.isLoading = true;
    this.loadingAction = 'test';
    this.optimizeResult = null;
    this.evalResult = null;
    this.render();
    this.attachListeners();

    try {
      const result = await outreachApi.testPrompt({
        prompt: this.prompt,
        input: structuredInput,
        platform: this.platform,
        promptId: this.promptId || undefined,
        promptVersion: this.promptVersion || undefined
      } as any);

      this.output = result.output;
      this.lastResult = result;
      this.saveState();
    } catch (error) {
      alert(`Test failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      this.isLoading = false;
      this.loadingAction = '';
      this.render();
      this.attachListeners();
    }
  }

  private async optimizePrompt(): Promise<void> {
    if (!this.prompt) {
      alert('Please enter a prompt to optimize');
      return;
    }

    this.isLoading = true;
    this.loadingAction = 'optimize';
    this.render();
    this.attachListeners();

    try {
      this.optimizeResult = await outreachApi.optimizePrompt({
        prompt: this.prompt,
        platform: this.platform
      });
    } catch (error) {
      alert(`Optimization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      this.isLoading = false;
      this.loadingAction = '';
      this.render();
      this.attachListeners();
    }
  }

  private async evaluateOutput(): Promise<void> {
    if (!this.output) {
      alert('No output to evaluate');
      return;
    }

    this.isLoading = true;
    this.loadingAction = 'evaluate';
    this.render();
    this.attachListeners();

    try {
      this.evalResult = await outreachApi.evaluateOutput({
        input: this.input,
        output: this.output
      });
    } catch (error) {
      alert(`Evaluation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      this.isLoading = false;
      this.loadingAction = '';
      this.render();
      this.attachListeners();
    }
  }

  private applyOptimized(): void {
    if (this.optimizeResult) {
      this.prompt = this.optimizeResult.optimizedPrompt;
      this.optimizeResult = null;
      this.render();
      this.attachListeners();
    }
  }

  private dismissOptimize(): void {
    this.optimizeResult = null;
    this.render();
    this.attachListeners();
  }

  private copyOutput(): void {
    if (this.output) {
      navigator.clipboard.writeText(this.output);
    }
  }

  private async loadLog(): Promise<void> {
    try {
      const data = await outreachApi.getTestLog({ limit: 100 });
      this.logEntries = data.entries;
    } catch (e) {
      console.error('Failed to load test log:', e);
    }
  }

  private async deleteLogEntry(id: string): Promise<void> {
    try {
      await outreachApi.deleteTestLogEntry(id);
      this.logEntries = this.logEntries.filter(e => e.id !== id);
      this.render();
      this.attachListeners();
    } catch (e) {
      alert('Failed to delete entry');
    }
  }

  private async clearLog(): Promise<void> {
    if (!confirm('Clear all test history?')) return;

    try {
      await outreachApi.clearTestLog();
      this.logEntries = [];
      this.render();
      this.attachListeners();
    } catch (e) {
      alert('Failed to clear log');
    }
  }

  private formatDate(timestamp: number): string {
    const date = new Date(timestamp);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}
